// src/pages/Landing.js
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BannerCarousel from "../components/BannerCarousel";
import GenericProductSectionProduct from "../components/Product";
import Feature from "../components/Feature";
import Blog from "../components/Blog";
import { productService } from "../services/productService";
import { Container, Alert, Spinner } from "react-bootstrap";

const LandingPage = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBackgroundUpdating, setIsBackgroundUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Collection configuration in the same order as your dashboard
  const collections = [
    { name: "New Set Promotion", label: "New Set Promotions", key: "newSetPromotions" },
    { name: "Popular Product", label: "Popular Product", key: "popularProducts" },
    { name: "Product Accessorie", label: "ACCESSORIES_PRODUCT", key: "accessoryProducts" }
  ];

  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);

    // Additional scroll after a tiny delay to ensure everything is rendered
    const timer = setTimeout(() => {
      const formContainer = document.querySelector('.delivery-form') ||
        document.querySelector('form') ||
        document.querySelector('.mt-top-cartpage') ||
        document.querySelector('.cart-items-sidebar');

      if (formContainer) {
        formContainer.scrollTop = 0;

        if (window.location.pathname.includes('checkout')) {
          const firstInput = document.querySelector('input, select, textarea');
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 100);
          }
        }
      }
    }, 50);

    // Fetch all products - show cache immediately, update in background
    const fetchProducts = async () => {
      const cacheKey = "cachedProducts";
      const cacheTimestampKey = "cachedProductsTimestamp";

      // Load from cache immediately (no loading state)
      const cachedProducts = localStorage.getItem(cacheKey);
      if (cachedProducts) {
        const parsedProducts = JSON.parse(cachedProducts);
        setProducts(parsedProducts);
        setError(null);
        // Don't set isLoading to true - show UI immediately
      } else {
        // Only show loading if no cache exists
        setIsLoading(true);
      }

      try {
        // Fetch fresh data in background
        setIsBackgroundUpdating(true);
        const data = await productService.getAllProducts();

        if (data && data.length > 0) {
          setProducts(data);
          // Update cache
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(cacheTimestampKey, Date.now().toString());
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        // Only show error if we have no cached data
        if (!localStorage.getItem(cacheKey)) {
          setError(t("failed_to_load_products"));
        }
      } finally {
        setIsLoading(false);
        setIsBackgroundUpdating(false);
      }
    };

    fetchProducts();

    return () => clearTimeout(timer);
  }, [t]);

  // Get products by collection
  const getProductsByCollection = (collectionName) => {
    if (!products.length) return [];
    return products.filter(product => product.collection === collectionName);
  };

  // Check if a collection has any products
  const hasCollectionProducts = (collectionName) => {
    return getProductsByCollection(collectionName).length > 0;
  };

  // Show loading ONLY on first visit when no cache exists
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // Only show error if we have no products and no cache
  if (error && products.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      </Container>
    );
  }

  // Check if any collection has products
  const hasAnyProducts = collections.some(collection =>
    hasCollectionProducts(collection.name)
  );

  if (!hasAnyProducts && products.length === 0) {
    return (
      <main className="landing-page mt-4" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
        <BannerCarousel />
        <Container className="text-center py-5">
          <h4>No products available at the moment</h4>
          <p>Please check back later for new collections!</p>
        </Container>
        <Feature />
        <Blog title={t('from_the_blog')} slice={[0, 8]} mode="row" />
      </main>
    );
  }

  return (
    <main className="landing-page mt-4" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <BannerCarousel />

      {/* Optional: Show background update indicator */}
      {isBackgroundUpdating && (
        <div className="position-fixed bottom-0 end-0 m-3" style={{ zIndex: 9999 }}>
          <div className="bg-primary text-white rounded-circle p-2 shadow">
            <Spinner animation="border" size="sm" />
          </div>
        </div>
      )}

      {/* Show collections in the specified order */}
      {collections.map(collection => {
        const collectionProducts = getProductsByCollection(collection.name);

        // Only render section if it has products
        if (collectionProducts.length === 0) return null;

        return (
          <GenericProductSectionProduct
            key={collection.key}
            title={t(collection.label)}
            collection={collection.name}
            mode="row"
          />
        );
      })}

      <Feature />
      <Blog title={t('from_the_blog')} slice={[0, 8]} mode="row" />
    </main>
  );
};

export default LandingPage;
